import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, ThermometerSun, Snowflake, RefreshCw, TrendingUp } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Modal, Selecter, Field } from "../pages/crm/crmui";
import { Btn } from "./common";

const BANT_BUDGET = ["", "> 10k", "3-10k", "< 3k", "sem orcamento"];
const BANT_AUTHORITY = ["", "decisor", "influenciador", "utilizador", "desconhecido"];
const BANT_NEED = ["", "forte", "moderada", "fraca", "ausente"];
const BANT_TIMING = ["", "0-30d", "1-3m", "3-6m", "6-12m", "> 12m", "indefinido"];
const CICLO = ["subscriber", "lead", "mql", "sql", "opportunity", "customer"];
const RATING = ["", "hot", "warm", "cold"];

const R_META = {
  hot: { cls: "bg-red-100 text-red-700 border-red-200", Icon: Flame },
  warm: { cls: "bg-amber-100 text-amber-700 border-amber-200", Icon: ThermometerSun },
  cold: { cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Snowflake },
};

export default function QualifyPanel({ account }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [promo, setPromo] = useState({ plataforma: "", mrr: 0, valor_unico: 0, nome: "" });
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    api.get("/platforms").then(({ data }) => setPlatforms(data || [])).catch(() => setPlatforms([]));
  }, []);

  const load = async () => {
    if (!account?.id) return;
    try {
      const { data } = await api.get(`/crm/qualify/${account.id}`);
      setQ(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [account?.id]); // eslint-disable-line

  const saveField = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/crm/qualify/${account.id}`, patch);
      setQ(data);
      toast.success("Qualificacao atualizada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const promover = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/qualify/${account.id}/promote-to-opportunity`, promo);
      toast.success("Promovido a oportunidade (proposta).");
      setShowPromote(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const demover = async () => {
    if (!window.confirm("Remover a classificacao de Oportunidade? As oportunidades em aberto (nao ganhas) associadas serao removidas do funil de vendas. A conta volta a ficar apenas como LEAD.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/qualify/${account.id}/demote-from-opportunity`);
      setQ(data);
      toast.success("Classificacao de Oportunidade removida. Conta reposta a LEAD.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!q) return <div className="text-[11px] text-slate-400 italic">A calcular score...</div>;
  const meta = R_META[q.rating] || R_META.cold;
  const bk = q.score_breakdown || {};

  return (
    <div data-testid="qualify-panel" className="space-y-2.5">
      {/* Score + rating */}
      <div className="flex items-center gap-3 bg-slate-50 border border-tint rounded-md p-2.5">
        <div className="text-4xl font-bold text-navy tabular-nums" data-testid="qualify-score">{q.lead_score}</div>
        <div className="flex-1">
          <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${meta.cls}`} data-testid="qualify-rating">
            <meta.Icon size={11} /> {(q.rating || "cold").toUpperCase()}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Ciclo sugerido: <strong>{q.ciclo_vida_sugerido}</strong> - Atividades: {q.n_atividades}
          </div>
        </div>
        <button onClick={load} title="Recalcular" data-testid="qualify-refresh" className="p-1 text-slate-500 hover:text-navy">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-5 gap-1 text-center">
        {[["Fit", bk.fit, 30], ["Dados", bk.completude, 20], ["Interac", bk.engagement, 30], ["Origem", bk.origem, 10], ["Recente", bk.recencia, 10]].map(([k, v, m]) => (
          <div key={k} className="border border-tint rounded p-1.5" title={`${v || 0}/${m}`}>
            <div className="text-[9px] uppercase text-slate-500">{k}</div>
            <div className="text-xs font-mono font-bold text-navy">{v || 0}/{m}</div>
          </div>
        ))}
      </div>

      {/* BANT */}
      <div className="pt-1">
        <div className="text-[10px] font-bold uppercase text-navy mb-1">BANT</div>
        <div className="grid grid-cols-2 gap-2">
          <Selecter label="Budget" value={q.bant?.budget || ""} onChange={(v) => saveField({ bant_budget: v })} options={BANT_BUDGET.map((x) => ({ value: x, label: x || "-" }))} testid="qualify-budget" />
          <Selecter label="Authority" value={q.bant?.authority || ""} onChange={(v) => saveField({ bant_authority: v })} options={BANT_AUTHORITY.map((x) => ({ value: x, label: x || "-" }))} testid="qualify-authority" />
          <Selecter label="Need" value={q.bant?.need || ""} onChange={(v) => saveField({ bant_need: v })} options={BANT_NEED.map((x) => ({ value: x, label: x || "-" }))} testid="qualify-need" />
          <Selecter label="Timing" value={q.bant?.timing || ""} onChange={(v) => saveField({ bant_timing: v })} options={BANT_TIMING.map((x) => ({ value: x, label: x || "-" }))} testid="qualify-timing" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Selecter label="Ciclo de vida" value={q.ciclo_vida_atual || "lead"} onChange={(v) => saveField({ ciclo_vida: v })} options={CICLO.map((x) => ({ value: x, label: x }))} testid="qualify-ciclo" />
        <Selecter label="Rating manual" value={q.rating_manual || ""} onChange={(v) => saveField({ rating_manual: v })} options={RATING.map((x) => ({ value: x, label: x || "auto" }))} testid="qualify-rating-manual" />
      </div>

      {account?.tipo !== "cliente" && q.ciclo_vida_atual !== "opportunity" && (
        <button type="button" onClick={() => setShowPromote(true)} data-testid="qualify-promote-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-semibold hover:bg-emerald-700">
          <TrendingUp size={14} /> Promover a Oportunidade
        </button>
      )}
      {account?.tipo !== "cliente" && q.ciclo_vida_atual === "opportunity" && (
        <button type="button" onClick={demover} disabled={busy} data-testid="qualify-demote-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-red-200 text-red-600 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-50">
          <TrendingUp size={14} className="rotate-180" /> {busy ? "..." : "Remover de Oportunidade"}
        </button>
      )}

      <Modal open={showPromote} onClose={() => setShowPromote(false)} title="Promover a Oportunidade" testid="promote-modal">
        <p className="text-xs text-muted2">Cria (ou promove) um deal no funil de vendas e coloca-o em estagio "proposta" com 60% de probabilidade.</p>
        <Field label="Nome (opcional)" value={promo.nome} onChange={(v) => setPromo({ ...promo, nome: v })} testid="promo-nome" />
        <Selecter label="Plataforma"
          value={promo.plataforma}
          onChange={(v) => setPromo({ ...promo, plataforma: v })}
          options={[{ value: "", label: "- Sem plataforma especifica -" }, ...platforms.map((p) => ({ value: p.slug || p.nome, label: p.nome || p.slug }))]}
          testid="promo-plataforma" />
        <div className="grid grid-cols-2 gap-2">
          <Field label="MRR (EUR)" type="number" value={promo.mrr} onChange={(v) => setPromo({ ...promo, mrr: parseFloat(v) || 0 })} testid="promo-mrr" />
          <Field label="Valor unico (EUR)" type="number" value={promo.valor_unico} onChange={(v) => setPromo({ ...promo, valor_unico: parseFloat(v) || 0 })} testid="promo-unico" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShowPromote(false)}>Cancelar</Btn>
          <Btn onClick={promover} testid="promo-save" disabled={busy}>{busy ? "..." : "Promover"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
